import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Search, ArrowRight } from "lucide-react";

export default function LandingPage({ onSubmit, onLogout }) {
  const [inputId, setInputId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputId.trim()) return;
    
    setIsSubmitting(true);
    onSubmit(inputId.trim());
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--sa-light)] safe-area-top safe-area-bottom">
      {/* Header with Logo */}
      <header className="bg-white shadow-sm">
        <div className="px-6 py-4 flex items-center justify-center relative">
          {onLogout && (
            <button
              type="button"
              data-testid="logout-btn"
              onClick={onLogout}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400 hover:text-[var(--sa-secondary)] transition-colors"
            >
              Abmelden
            </button>
          )}
          <img 
            src="/safety-assist-logo.png" 
            alt="SafetyAssist" 
            className="h-10 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center gap-2">
            <div className="w-8 h-8 bg-[var(--sa-primary)] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SA</span>
            </div>
            <span className="font-bold text-[var(--sa-secondary)] text-lg">SafetyAssist</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-12 animate-slide-up">
        <div className="max-w-md mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-[var(--sa-secondary)] mb-3">
              Objekt-Verwaltung
            </h1>
            <p className="text-gray-600">
              Gib die ID aus dem QR-Code ein, um das Objekt zu verwalten.
            </p>
          </div>

          {/* Search Card */}
          <Card className="p-6 bg-white shadow-lg border-0 rounded-2xl">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-[var(--sa-secondary)]">
                  SafetyAssist-ID eingeben
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    data-testid="id-input"
                    type="text"
                    placeholder="z.B. abc123xyz"
                    value={inputId}
                    onChange={(e) => setInputId(e.target.value)}
                    className="h-14 pl-12 pr-4 text-lg bg-[var(--sa-light)] border-0 rounded-xl focus:ring-2 focus:ring-[var(--sa-primary)]"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Die ID findest du unter dem QR-Code oder im Link.
                </p>
              </div>

              <Button
                data-testid="search-btn"
                type="submit"
                disabled={!inputId.trim() || isSubmitting}
                className="w-full h-14 bg-[var(--sa-primary)] hover:bg-[var(--sa-primary-dark)] text-white rounded-xl font-semibold text-base btn-press"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Suche...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>Objekt suchen</span>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </main>

      {/* Footer with extra bottom padding */}
      <footer className="p-4 pb-28 text-center">
        <p className="text-xs text-gray-400">
          © {new Date().getFullYear()} SafetyAssist
        </p>
      </footer>
    </div>
  );
}
